import { useEffect, type ReactNode } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { PageHintButton } from './PageHintButton';
import { resolvePageHintKey } from '../lib/pageHints';
import { usePageTitleContext } from '../layout/PageTitleContext';

interface PageHeaderProps {
  /** Mono uppercase eyebrow above the title (section context). */
  readonly eyebrow?: string;
  /** Bold display title. */
  readonly title: string;
  /** Optional supporting line under the title. */
  readonly subtitle?: string;
  /** Optional right-aligned actions (buttons, filters). */
  readonly actions?: ReactNode;
}

/**
 * Editorial admin page header.
 *
 * Inside `AdminShell` (the live app) the title + ⓘ hint are lifted into the top
 * bar (`PageTitleContext`), so the body renders only the actions / subtitle /
 * eyebrow row — pages start at their content with no big H1 eating vertical
 * space. When rendered OUTSIDE the shell (a page mounted directly in a unit
 * test → no provider) it falls back to the classic full header (eyebrow + H1 +
 * ⓘ + subtitle + actions), so standalone renders and existing tests are
 * unaffected.
 */
export function PageHeader({ eyebrow, title, subtitle, actions }: PageHeaderProps) {
  const { t } = useI18n();
  const ctx = usePageTitleContext();
  const setTitle = ctx?.setTitle;

  // Register this page's title with the shell so the top bar can render it.
  useEffect(() => {
    if (!setTitle) return undefined;
    setTitle(title);
    return () => setTitle(null);
  }, [setTitle, title]);

  // ── In-shell: title + ⓘ live in the top bar; render only the actions row ──
  if (ctx) {
    if (!eyebrow && !subtitle && !actions) return null;
    return (
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0 space-y-1">
          {eyebrow ? (
            <p className="font-mono text-mono uppercase tracking-[0.18em] text-accent">{eyebrow}</p>
          ) : null}
          {subtitle ? <p className="max-w-2xl text-body text-ink-2">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </header>
    );
  }

  // ── Standalone (tests / no shell): the classic full header with the title ──
  const hintKey =
    typeof window === 'undefined' ? null : resolvePageHintKey(window.location.pathname);
  return (
    <header className="flex flex-wrap items-end justify-between gap-4 border-b border-hairline pb-5">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="font-mono text-mono uppercase tracking-[0.18em] text-accent">{eyebrow}</p>
        ) : null}
        <div className="mt-1.5 flex items-center gap-2">
          <h1 className="font-display text-h2 font-black tracking-tight text-ink">{title}</h1>
          {hintKey ? <PageHintButton text={t(hintKey)} label={t('admin:hints.aria')} /> : null}
        </div>
        {subtitle ? <p className="mt-1.5 max-w-2xl text-body text-ink-2">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}
